import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";
import { FileSearch, Search, Filter, Download } from "lucide-react";

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [options, setOptions] = useState({ actions: [], data_types: [] });
  const [q, setQ] = useState("");
  const [action, setAction] = useState("");
  const [dataType, setDataType] = useState("");
  const [home, setHome] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [limit] = useState(200);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit };
      if (q) params.q = q;
      if (action) params.action = action;
      if (dataType) params.data_type = dataType;
      if (home) params.home = home;
      if (start) params.start = start;
      if (end) params.end = end;
      const { data } = await api.get("/audit-logs", { params });
      setLogs(data);
    } finally { setLoading(false); }
  }, [q, action, dataType, home, start, end, limit]);

  useEffect(() => { api.get("/audit-logs/options").then(({ data }) => setOptions(data)); }, []);
  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    if (!logs.length) return;
    const rows = logs.map((l) => ({
      timestamp: l.timestamp, user_name: l.user_name || "",
      user_role: l.user_role || "", action: l.action,
      data_type: l.data_type || "", record_id: l.record_id || "",
      home: l.home || "",
    }));
    const headers = Object.keys(rows[0]);
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const lines = [headers.join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(","))];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `nv-audit-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const groupedTotals = useMemo(() => {
    const byAction = {};
    logs.forEach((l) => { byAction[l.action] = (byAction[l.action] || 0) + 1; });
    return Object.entries(byAction).sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [logs]);

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-small-caps">Compliance</p>
          <h1 className="text-h1 mt-1">Audit Logs</h1>
          <p className="text-sm text-slate-500 mt-1">Filter, search, and export. Showing up to {limit} most recent matching events.</p>
        </div>
        <button onClick={exportCSV} disabled={!logs.length} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-sky-800 hover:bg-sky-900 text-white text-sm font-semibold disabled:opacity-50" data-testid="audit-export-csv">
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div className="nv-card p-4">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="audit-filters">
          <label className="block col-span-2 md:col-span-2">
            <span className="text-small-caps inline-flex items-center gap-1.5"><Search size={11} /> Keyword</span>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="user, action, record id…" className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="audit-search" />
          </label>
          <label className="block">
            <span className="text-small-caps inline-flex items-center gap-1.5"><Filter size={11} /> Action</span>
            <select value={action} onChange={(e) => setAction(e.target.value)} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="audit-action">
              <option value="">All</option>
              {options.actions.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-small-caps">Type</span>
            <select value={dataType} onChange={(e) => setDataType(e.target.value)} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="audit-type">
              <option value="">All</option>
              {options.data_types.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-small-caps">From</span>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="audit-start" />
          </label>
          <label className="block">
            <span className="text-small-caps">To</span>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="audit-end" />
          </label>
        </div>
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <span className="rounded-full bg-slate-50 text-slate-600 border border-slate-200 px-2.5 py-1 font-medium">{loading ? "Loading…" : `${logs.length} events`}</span>
          {groupedTotals.map(([a, count]) => (
            <span key={a} className="rounded-full bg-sky-50 text-sky-700 border border-sky-200 px-2.5 py-1 font-medium">{a}: {count}</span>
          ))}
        </div>
      </div>

      <div className="nv-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="audit-table">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-5 py-3 text-left">When</th>
              <th className="px-3 py-3 text-left">User</th>
              <th className="px-3 py-3 text-left">Action</th>
              <th className="px-3 py-3 text-left">Type</th>
              <th className="px-3 py-3 text-left">Home</th>
              <th className="px-3 py-3 text-left">Record</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((l) => (
              <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                <td className="px-5 py-3 text-slate-500 text-xs whitespace-nowrap">{new Date(l.timestamp).toLocaleString()}</td>
                <td className="px-3 py-3 text-slate-800">{l.user_name || "—"} <span className="text-xs text-slate-400">({l.user_role})</span></td>
                <td className="px-3 py-3 text-slate-700 font-medium">{l.action}</td>
                <td className="px-3 py-3 text-slate-600">{l.data_type}</td>
                <td className="px-3 py-3 text-slate-600">{l.home || "—"}</td>
                <td className="px-3 py-3 text-slate-500 text-xs font-mono break-all">{l.record_id || "—"}</td>
              </tr>
            ))}
            {!loading && logs.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-500"><FileSearch size={28} className="mx-auto text-slate-300 mb-2" />No audit events match the filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
